import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, Search, Plus, Music, Lock, Globe, 
  Calendar, MessageSquare, UserPlus, ChevronRight,
  MapPin
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import Button from '../components/Button';
import Card from '../components/Card';
import { format } from 'date-fns';
import UserName from '../components/UserName';
import CreateCommunityModal from '../components/CreateCommunityModal';
import supabase from "../lib/supabase";
import type { Community } from '../types';

export default function Communities() {
  const { user } = useAuthStore();
  const [communities, setCommunities] = useState<Community[]>([]);
  const [userMemberships, setUserMemberships] = useState<Record<string, boolean>>({});
  const [pendingRequests, setPendingRequests] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedGenre, setSelectedGenre] = useState<string | null>(null);
  const [isJoining, setIsJoining] = useState<string | null>(null);
  const [countries, setCountries] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [filteredCities, setFilteredCities] = useState<string[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>('all');
  const [selectedCity, setSelectedCity] = useState<string>('all');
  const [allGenres, setAllGenres] = useState<string[]>([]);

  // Fetch communities and user memberships
  const fetchCommunities = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // First get all communities without filters to extract all possible genres and locations
      const { data: allCommunitiesData, error: allCommunitiesError } = await supabase
        .from('communities')
        .select(`
          *,
          leader:leader_id(
            id,
            full_name,
            display_name,
            verified
          ),
          members:community_members(count)
        `);

      if (allCommunitiesError) throw allCommunitiesError;

      // Extract all unique genres
      const genresSet = new Set<string>();
      allCommunitiesData?.forEach(community => {
        if (community.genres && community.genres.length > 0) {
          community.genres.forEach(genre => genresSet.add(genre));
        }
      });
      setAllGenres(Array.from(genresSet).sort());

      // Extract all unique locations
      const locationMap = new Map<string, Set<string>>();
      const allCitiesSet = new Set<string>();

      allCommunitiesData?.forEach(c => {
        if (c.location && c.location_enabled) {
          const [city, country] = c.location.split(',').map(part => part.trim());
          if (city && country) {
            if (!locationMap.has(country)) {
              locationMap.set(country, new Set());
            }
            locationMap.get(country)?.add(city);
            allCitiesSet.add(city);
          }
        }
      });

      // Sort countries and all cities
      const uniqueCountries = Array.from(locationMap.keys()).sort();
      const uniqueCities = Array.from(allCitiesSet).sort();

      setCountries(uniqueCountries);
      setCities(uniqueCities);
      setFilteredCities(uniqueCities); // Initialize filtered cities with all cities

      // Now apply filters for the displayed communities
      let query = supabase
        .from('communities')
        .select(`
          *,
          leader:leader_id(
            id,
            full_name,
            display_name,
            verified
          ),
          members:community_members(count)
        `);

      // Apply search filter if query exists
      if (searchQuery) {
        query = query.ilike('name', `%${searchQuery}%`);
      }

      // Apply genre filter if selected
      if (selectedGenre !== null && selectedGenre !== 'all') {
        query = query.contains('genres', [selectedGenre]);
      }

      // Apply location filters if selected
      if (selectedCountry !== 'all' || selectedCity !== 'all') {
        query = query.eq('location_enabled', true);
        
        if (selectedCountry !== 'all') {
          query = query.ilike('location', `%, ${selectedCountry}`);
        }
        
        if (selectedCity !== 'all') {
          query = query.ilike('location', `${selectedCity},%`);
        }
      }

      const { data: communitiesData, error: communitiesError } = await query;

      if (communitiesError) throw communitiesError;

      // If user is logged in, fetch their memberships and pending requests
      if (user) {
        // Get memberships
        const { data: membershipsData } = await supabase
          .from('community_members')
          .select('community_id')
          .eq('user_id', user.id)

        const membershipMap = membershipsData?.reduce((acc, membership) => {
          acc[membership.community_id] = true;
          return acc;
        }, {} as Record<string, boolean>) || {};

        setUserMemberships(membershipMap);

        // Get pending requests
        const { data: requestsData } = await supabase
          .from('membership_requests')
          .select('community_id')
          .eq('user_id', user.id)
          .eq('status', 'pending');

        const pendingRequestsMap = requestsData?.reduce((acc, request) => {
          acc[request.community_id] = true;
          return acc;
        }, {} as Record<string, boolean>) || {};

        setPendingRequests(pendingRequestsMap);
      }

      // Update filtered cities based on selected country
      if (selectedCountry !== 'all') {
        const citiesInCountry = Array.from(locationMap.get(selectedCountry) || []).sort();
        setFilteredCities(citiesInCountry);
      } else {
        setFilteredCities(uniqueCities);
      }

      setCommunities(communitiesData || []);
    } catch (err: any) {
      console.error('Error fetching communities:', err);
      setError('Failed to load communities. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, selectedGenre, selectedCountry, selectedCity, user]);

  useEffect(() => {
    fetchCommunities();
  }, [fetchCommunities]);

  // Update filtered cities when country selection changes
  useEffect(() => {
    if (selectedCountry === 'all') {
      setFilteredCities(cities);
    } else {
      // Find cities that belong to the selected country
      const citiesInCountry = communities
        .filter(c => c.location && c.location_enabled && c.location.includes(`, ${selectedCountry}`))
        .map(c => {
          const [city] = c.location.split(',').map(part => part.trim());
          return city;
        })
        .filter((city, index, self) => city && self.indexOf(city) === index)
        .sort();
      
      setFilteredCities(citiesInCountry);
      
      // Reset city selection if the current selected city is not in the filtered list
      if (selectedCity !== 'all' && !citiesInCountry.includes(selectedCity)) {
        setSelectedCity('all');
      }
    }
  }, [selectedCountry, communities, cities, selectedCity]);

  const handleJoinCommunity = async (communityId: string) => {
    try {
      if (!user) {
        window.location.href = '/auth';
        return;
      }

      setIsJoining(communityId);

      const { data: community } = await supabase
        .from('communities')
        .select('is_private')
        .eq('id', communityId)
        .single();

      if (!community) throw new Error('Community not found');

      if (!community.is_private) {
        // For public communities, directly join
        const { error: joinError } = await supabase
          .from('community_members')
          .insert({
            community_id: communityId,
            user_id: user.id,
            role: 'member'
          });

        if (joinError) throw joinError;

        // Update local state
        setUserMemberships(prev => ({
          ...prev,
          [communityId]: true
        }));
      } else {
        // For private communities, create membership request
        const { error: requestError } = await supabase
          .from('membership_requests')
          .insert({
            community_id: communityId,
            user_id: user.id,
            status: 'pending'
          });

        if (requestError) throw requestError;

        // Update local state
        setPendingRequests(prev => ({
          ...prev,
          [communityId]: true
        }));
      }

      // Refresh communities to update member count
      await fetchCommunities();
    } catch (error: any) {
      console.error('Error joining community:', error);
      setError(error.message);
    } finally {
      setIsJoining(null);
    }
  };

  // Filter communities based on search query and genre
  const filteredCommunities = communities;

  return (
    <div className="min-h-screen p-4 bg-neutral">
      <div className="flex flex-col space-y-6">
        <h1 className="text-3xl font-bold font-dillan text-surface-light text-center">Communities</h1>
        
        <div className="flex flex-wrap items-start justify-between">
          {/* Left side - Create Community and Search */}
          <div className="flex flex-wrap items-start gap-4">
            {user && (
              <Button 
                variant="primary" 
                className="w-full py-2 text-m bg-secondary text-surface-light border-4 border-secondary hover:text-primary hover:bg-primary-light hover:border-primary-light"
                onClick={() => setShowCreateModal(true)}
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Create Community
              </Button>
            )}
            
            <div className="relative w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-neutral" />
              </div>
              <input
                type="text"
                placeholder="Search communities"
                className="block w-full pl-10 pr-3 py-2 rounded-full border-4 border-surface-light bg-surface-light text-neutral text-m placeholder:text-neutral focus:border-surface-light focus:ring-0 focus:outline-none"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Right side - Filter Dropdowns */}
          <div className="flex flex-nowrap justify-center p-0 m-0 -mx-4 mt-4 w-full mx-auto">
            <select
              className="w-28 rounded-full bg-neutral text-surface-light font-dillan focus:outline-none transition-colors truncate overflow-hidden whitespace-nowrap"
              value={selectedGenre || 'all'}
              onChange={(e) => setSelectedGenre(e.target.value === 'all' ? null : e.target.value)}
            >
              <option value="all">All Genres</option>
              {allGenres.map((genre) => (
                <option key={genre} value={genre}>
                  {genre}
                </option>
              ))}
            </select>

            <select
              className="w-32 rounded-full bg-neutral text-surface-light font-dillan focus:outline-none transition-colors truncate overflow-hidden whitespace-nowrap"
              value={selectedCountry}
              onChange={(e) => {
                setSelectedCountry(e.target.value);
                // City will be reset in the useEffect if needed
              }}
            >
              <option value="all">All Countries</option>
              {countries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>

            <select
              className="w-24 rounded-full bg-neutral text-surface-light font-dillan focus:outline-none transition-colors truncate overflow-hidden whitespace-nowrap"
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
            >
              <option value="all">All Cities</option>
              {filteredCities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div className="bg-secondary-dark bg-opacity-10 text-secondary-dark p-4 rounded-xl">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center items-center min-h-screen bg-neutral">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-surface mx-auto mb-4"></div>
          </div>
        ) : filteredCommunities.length === 0 ? (
          <div className="text-center min-h-screen bg-neutral">
            <Users className="w-16 h-16 text-surface mx-auto opacity-50" />
            <p className="text-lg text-surface mb-2">No communities found</p>
            <p className="text-sm text-surface">
              {searchQuery || selectedGenre || selectedCountry !== 'all' || selectedCity !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Be the first to create a community!'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {filteredCommunities.map((community) => {
              const isMember = userMemberships[community.id];
              const isPendingRequest = pendingRequests[community.id];
              
              return (
                <Card key={community.id} hover={false} className="relative p-0">
                  {/* Community Header */}
                  <Link to={`/communities/${community.id}`} className="block">
                    <div className="relative relative p-4 pb-0">
                      <div className="w-full h-32 bg-primary bg-opacity-10 rounded-t-xl flex items-center justify-center">
                        {community.cover_image ? (
                          <img
                            src={community.cover_image}
                            alt={community.name}
                            className="w-full h-32 rounded-t-xl object-cover"
                          />
                        ) : (
                          <Users className="w-12 h-12 text-primary opacity-20" />
                        )}
                      </div>
                      <div className="absolute top-6 right-6 z-10">
                        {community.is_private ? (
                          <div className="bg-primary bg-opacity-90 backdrop-blur-sm text-surface-light rounded-full px-3 py-1 text-sm font-medium flex items-center">
                            <Lock className="w-4 h-4 mr-1.5" />
                            Private
                          </div>
                        ) : (
                          <div className="bg-primary bg-opacity-90 backdrop-blur-sm text-surface-light rounded-full px-3 py-1 text-sm font-medium flex items-center">
                            <Globe className="w-4 h-4 mr-1.5" />
                            Public
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>

                  <div className="p-4 w-full">
                    {/* Community Name and Genres */}
                    <div className="flex justify-between items-center w-full">
                      <Link to={`/communities/${community.id}`} className="block">
                        <h2 className="font-dillan text-xl font-semibold text-primary hover:text-accent transition-colors">
                          {community.name}
                        </h2>
                      </Link>
                      
                      <div className="flex items-center text-primary">
                        <Users className="w-4 h-4 mr-1.5" />
                        {community.members[0]?.count || 0} members
                       </div>
                    </div>
                    {community.genres && community.genres.length > 0 && (
                      <div className="w-full text-xs italic font-medium text-neutral mb-4">
                        {community.genres.slice(0, 3).join(", ")}
                        {community.genres.length > 3 && `, +${community.genres.length - 3} more`}
                      </div>
                    )}

                    {/* Community Description */}
                    <p className="text-primary text-sm mb-4 break-words whitespace-pre-line">
                      {community.description}
                    </p>

                    {/* Community Stats */}
                    <div className="flex items-center justify-between text-sm text-neutral mb-4">
                      {/* Location if available */}
                      {community.location && community.location_enabled && (
                        <div className="flex items-center text-neutral text-sm mb-4">
                          <MapPin className="w-4 h-4 mr-1.5" />
                          {community.location}
                        </div>
                      )}
                    </div>

                    {/* Action Button */}
                    <div className="mt-auto pt-4 flex justify-center"> 
                      {user ? (
                        isMember ? (
                          <Link 
                            to={`/communities/${community.id}`}
                            className="w-full inline-flex items-center justify-center px-4 py-2 rounded-full bg-secondary border-4 border-secondary text-base text-surface-light text-m uppercase font-medium hover:bg-primary-light hover:border-primary-light hover:text-primary transition-colors mb-4"
                          >
                            <MessageSquare className="w-4 h-4 mr-1.5" />
                            View Community
                          </Link>
                        ) : isPendingRequest ? (
                          <Button
                            className="w-full inline-flex items-center justify-center px-4 py-2 rounded-full bg-secondary border-4 border-secondary text-base text-surface-light uppercase font-medium hover:bg-primary-light hover:border-primary-light hover:text-primary transition-colors mb-4"
                            disabled={true}
                          >
                            <UserPlus className="w-4 h-4 mr-1.5" />
                            Request Pending
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full inline-flex items-center justify-center px-4 py-2 rounded-full bg-secondary border-4 border-secondary text-base text-surface-light uppercase font-medium hover:bg-primary-light hover:border-primary-light hover:text-primary transition-colors mb-4"
                            onClick={() => handleJoinCommunity(community.id)}
                            disabled={isJoining === community.id}
                          >
                            <UserPlus className="w-4 h-4 mr-1.5" />
                            {isJoining === community.id ? 'Joining...' : 
                             community.is_private ? 'Request to Join' : 'Join Community'}
                          </Button>
                        )
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          className="w-full"
                          as="a"
                          href="/auth"
                        >
                          Sign in to join
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Community Modal */}
      {showCreateModal && (
        <CreateCommunityModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={fetchCommunities}
        />
      )}
    </div>
  );
}